import { type Node, type Edge } from '@xyflow/react';
import { edgeDisplayName, edgeVisual } from '@/components/canvas/edgeTypes';
import type { MarketCategory } from '@/lib/market-categories';
import type { CreateNodeParams, UpdateNodeParams, CreateEdgeParams, AssumptionVerdict } from '@/types/api';
import type { RespondentContext } from '@/types/dialog';
import type { CustomerSegment } from '@/types/project';

interface DbNode {
  id: string;
  projectId: string;
  type: string;
  title: string;
  description?: string;
  position: { x: number; y: number };
  metadata?: NodeMetadata;
  [key: string]: unknown;
}

export interface NodeMetadata {
  // 'dialog' and 'idea-capture' are the known sources today; the
  // (string & {}) pattern keeps them as literal hints for autocomplete
  // without forbidding future values. A plain `string` would collapse the
  // literals away entirely.
  source?: 'dialog' | 'idea-capture' | (string & {});
  dialogId?: string;
  respondentEmail?: string;
  respondentName?: string;
  // Anonymous (shared-link) submissions carry no email: the dialog lambda
  // writes respondentName:'Anonymous' plus this stable per-session id, so two
  // different anonymous people stay distinct. See deriveRespondentKey.
  respondentActorId?: string;
  draftId?: string;
  edited?: boolean;
  // Market finding attrs (type === 'market_signal'). `source` is 'ai' or
  // 'paste' here. `category` is the research lens the finding was tagged with
  // (see lib/market-categories.ts); absent means uncategorised.
  category?: MarketCategory | (string & {});
  // Frozen snapshot of the respondent's context at submission time. Used by
  // the canvas to filter the graph by segment. Anonymous (shared-link) and
  // legacy nodes carry no segment and surface as "Unsegmented" in the
  // filter UI.
  segment?: RespondentContext;
  // Idea node attrs (source === 'idea-capture'). Aspiration rides as plain
  // text; customerSegment is the PM's hypothesised target audience and is
  // separate from `segment` above (per-respondent context).
  aspiration?: string;
  customerSegment?: CustomerSegment;
  // Assumption node attrs (type === 'assumption'). importance × certainty are
  // the PM-owned classification (the 2×2 board's two axes); riskLevel is derived
  // from them and stored so step 7 and the deck can read it directly. aiFlagged
  // marks an AI-asserted hard stop; aiReasoning is the model's rationale.
  // impossibleDismissed is the PM's reversible override of an
  // `aiFlagged: 'impossible'` call — the AI's flag is a permanent record, so
  // dismissing it (and restoring it) is separate, PM-owned state.
  importance?: 'high' | 'low';
  certainty?: 'known' | 'unknown';
  riskLevel?: 'critical' | 'risky' | 'fine';
  aiFlagged?: 'impossible';
  aiReasoning?: string;
  impossibleDismissed?: boolean;
  // The PM's optional note on why they disagreed with the AI's impossible
  // call. Set only while dismissing (never on restore), so it survives as a
  // durable record even after the flag is dismissed again later.
  dismissReason?: string;
  // Optional note recorded when the PM downgrades a critical assumption —
  // same history posture as dismissReason (survives later risk changes).
  downgradeReason?: string;
  // Step 7's call on this assumption, with the evidence it rests on. Written by the
  // alignment lambda (nested path SET), removed by "Reopen". Absent = still open.
  verdict?: AssumptionVerdict;
  // Synthesised-node attrs (source === 'synthesis'): a promoted synthesis
  // cluster, now a native job/challenge/opportunity node. Carries the raw dialog
  // items it groups (memberItemIds), how many distinct respondents raised it
  // (respondentCount, the "N of M" numerator), and the respondent total at the
  // run that produced it (respondentTotal, the "N of M" denominator — absent on
  // nodes promoted before this was captured).
  memberItemIds?: string[];
  respondentCount?: number;
  respondentTotal?: number;
  itemCount?: number;
  // Market-signal node attrs (type === 'market_signal'): external research
  // citations backing the finding. Drives the conviction source-count tick.
  citations?: Array<{ url: string; title?: string }>;
}

interface DbEdge {
  id: string;
  projectId: string;
  sourceNodeId: string;
  targetNodeId: string;
  edgeType: string;
  [key: string]: unknown;
}

export function toReactFlowNode(dbNode: DbNode): Node {
  return {
    id: dbNode.id,
    type: dbNode.type,
    position: dbNode.position,
    data: {
      label: dbNode.title,
      content: dbNode.description || '',
      metadata: dbNode.metadata,
    },
  };
}

export function toApiNode(rfNode: Node, projectId: string): CreateNodeParams {
  const data = rfNode.data as Record<string, unknown>;
  return {
    id: rfNode.id,
    projectId,
    type: rfNode.type || 'capability',
    title: (data.label as string) || '',
    description: typeof data.content === 'string' ? data.content : undefined,
    position: rfNode.position,
  };
}

export function toUpdateNode(rfNode: Node, projectId: string): UpdateNodeParams {
  const data = rfNode.data as Record<string, unknown>;
  return {
    id: rfNode.id,
    projectId,
    title: (data.label as string) || '',
    description: typeof data.content === 'string' ? data.content : undefined,
    position: rfNode.position,
  };
}

export function toReactFlowEdge(dbEdge: DbEdge): Edge {
  return {
    id: dbEdge.id,
    source: dbEdge.sourceNodeId,
    target: dbEdge.targetNodeId,
    // `label` is presentation; the stored relationship rides in `data.edgeType`
    // (the round-trip source of truth — see toApiEdge).
    label: edgeDisplayName(dbEdge.edgeType),
    data: { edgeType: dbEdge.edgeType },
    // Bézier (React Flow's 'default') over orthogonal 'smoothstep': each edge
    // takes a distinct arc, so an edge is traceable to its endpoints without
    // the impact-analysis highlight. Handles still steer the curve (spine
    // right→left, overlay bottom→top).
    type: 'default',
    ...edgeVisual(dbEdge.edgeType),
  };
}

export function toApiEdge(rfEdge: Edge, projectId: string): CreateEdgeParams {
  const edgeType = (rfEdge.data as { edgeType?: string } | undefined)?.edgeType;
  return {
    id: rfEdge.id,
    projectId,
    sourceNodeId: rfEdge.source,
    targetNodeId: rfEdge.target,
    // Edges drawn directly on the canvas carry no type yet → default.
    edgeType: edgeType || 'depends-on',
  };
}
